import { supabase } from '@common/api/authService';
import Nango from '@nangohq/frontend';
import {
    useEffect,
    useRef,
    useState
} from 'react';

// Clean ID helper function
const cleanId = (idStr: any): string => {
    if (!idStr) return "";
    return String(idStr).replace("customers/", "").trim();
};

// Account data interface matching the reference design
export interface AccountDetail {
    account_id: string;
    name: string;
    currency_code: string;
    timezone: string;
    is_manager: string;
}

export interface CustomerGroup {
    account_id: string;
    name: string;
    currency_code: string;
    timezone: string;
    accounts: AccountDetail[];
}

export interface NangoIntegrations {
    googleAds: {
        connected: boolean;
        availableCustomers: CustomerGroup[];
        hasFetchedAccounts: boolean;
        accountsLoading: boolean;
    };
    bigCommerce: {
        connected: boolean;
        data?: any;
    };
    connectGoogleAds: () => Promise<any>;
    disconnectGoogleAds: () => Promise<void>;
    connectBigCommerce: (connectionConfig?: any) => Promise<any>;
    disconnectBigCommerce: () => Promise<void>;
    integrationData: any;
}

export const useNango = (user: any) => {
    const [googleAdsConnected, setGoogleAdsConnected] = useState(false);
    const [bigCommerceConnected, setBigCommerceConnected] = useState(false);
    const [integrationData, setIntegrationData] = useState<any>(null);
    const [bigCommerceData, setBigCommerceData] = useState<any>(null);
    const initRef = useRef(false);

    // Fetch user integrations from backend
    const fetchUserIntegrations = async () => {
        try {
            const token = (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) return null;

            const response = await fetch(`${import.meta.env.VITE_OAUTH_HOST}/api/nango/user/integrations`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const resBody = await response.json();
                
                // Update: 2026-01-04 TraeAI - Adapted to new API response structure for integrations check
                // Parse google-ads and bigcommerce connection status from the new data structure
                if (resBody.data) {
                    const googleAdsData = resBody.data['google-ads'] || [];
                    const bigCommerceList = resBody.data['bigcommerce'] || [];

                    // Check connection status by 'connected' field
                    const activeGoogleAds = googleAdsData.find((item: any) => item.connected === true);
                    const activeBigCommerce = bigCommerceList.find((item: any) => item.connected === true);

                    setGoogleAdsConnected(!!activeGoogleAds);
                    setBigCommerceConnected(!!activeBigCommerce);

                    // Set integration data for Google Ads compatibility (preserving previous account display logic)
                    if (activeGoogleAds) {
                        setIntegrationData(activeGoogleAds);
                    } else {
                        setIntegrationData(null);
                    }

                    // Set BigCommerce data
                    if (activeBigCommerce) {
                        setBigCommerceData(activeBigCommerce);
                    } else {
                        setBigCommerceData(null);
                    }

                    return {
                        googleAds: !!activeGoogleAds,
                        bigCommerce: !!activeBigCommerce
                    };
                } else {
                    setIntegrationData(null);
                    setBigCommerceData(null);
                    setGoogleAdsConnected(false);
                    setBigCommerceConnected(false);
                    return { googleAds: false, bigCommerce: false };
                }
            } else {
                // Handle error responses
                try {
                    const errorBody = await response.json();
                    // Specific handling for "No Google Ads account found"
                    if (errorBody?.message?.includes('No Google Ads account found')) {
                        setGoogleAdsConnected(false);
                        return { googleAds: false, bigCommerce: false };
                    }
                } catch (e) {
                    // Ignore JSON parse error for non-JSON error responses
                }
                
                console.error('获取集成状态失败:', response.status, response.statusText);
                setGoogleAdsConnected(false);
                return null;
            }
        } catch (err) {
            console.error('获取集成状态失败:', err);
            setGoogleAdsConnected(false);
            return null;
        }
    };

    // Initialize Nango and check existing integrations
    useEffect(() => {
        if (initRef.current) return;
        if (!user?.id) return;
        initRef.current = true;

        fetchUserIntegrations();
    }, [user]);

    const [googleAdsAvailableCustomers, setGoogleAdsAvailableCustomers] = useState<CustomerGroup[]>([]);
    const [googleAdsAccountsLoading, setGoogleAdsAccountsLoading] = useState(false);
    const [googleAdsHasFetchedAccounts, setGoogleAdsHasFetchedAccounts] = useState(false);

    // Fetch Google Ads accounts from backend API
    const fetchGoogleAdsAccounts = async (): Promise<CustomerGroup[]> => {
        try {
            setGoogleAdsAccountsLoading(true);
            const token = (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) return [];

            const resp = await fetch(`${import.meta.env.VITE_OAUTH_HOST}/api/nango/user/google-ads/accounts`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (resp.ok) {
                const body = await resp.json();
                const list = body?.data?.accounts ?? body?.result?.accounts ?? body?.accounts ?? [];

                // Transform the data to match the expected format
                const transformedData: CustomerGroup[] = Array.isArray(list) ? list.map((acc: any) => ({
                    account_id: acc.login_customer_id,
                    name: acc.login_customer_name || `Account ${acc.login_customer_id}`,
                    currency_code: "",
                    timezone: "",
                    accounts: acc.sub_accounts.map((sub: any) => ({
                        account_id: sub.id,
                        name: sub.name === 'null' ? `Account ${sub.id}` : sub.name,
                        currency_code: "",
                        timezone: sub.time_zone || "",
                        is_manager: sub.is_manager ? "true" : "false"
                    }))
                })) : [];
                setGoogleAdsAvailableCustomers(transformedData);
                return transformedData;
            }
            return [];
        } catch (e) {
            console.error('获取账户失败:', e);
            return [];
        } finally {
            setGoogleAdsAccountsLoading(false);
            setGoogleAdsHasFetchedAccounts(true);
        }
    };

    useEffect(() => {
        if (googleAdsConnected && !googleAdsHasFetchedAccounts) {
            fetchGoogleAdsAccounts();
        }
    }, [googleAdsConnected, googleAdsHasFetchedAccounts]);

    // Connect to Google Ads using Nango
    const connectGoogleAds = async () => {
        try {

            const token = (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) {
                throw new Error('No auth token found');
            }

            const tokenResponse = await fetch(`${import.meta.env.VITE_OAUTH_HOST}/api/nango/connect-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({integration_id: 'google-ads'})
            });

            const tokenText = await tokenResponse.text();
            if (!tokenResponse.ok) throw new Error(`Token请求失败: ${tokenText}`);

            const tokenBody = JSON.parse(tokenText);
            const nangoToken = tokenBody.data?.token || tokenBody.result?.token;

            if (!nangoToken) throw new Error('未获取到 Token');


            const nango = new Nango({connectSessionToken: nangoToken});


            const result = await nango.auth('google-ads');

            
            // Only proceed if we have a connection ID
            if(result?.connectionId){
                // Do NOT set connected yet. Check for accounts first.
                const accounts = await fetchGoogleAdsAccounts();
                
                if (accounts && accounts.length > 0) {
                    setGoogleAdsConnected(true);
                    // Update integration data as well
                    fetchUserIntegrations();
                } else {
                    console.warn('Connected to Nango but no Google Ads accounts found.');
                    setGoogleAdsConnected(false);
                    throw new Error('No Google Ads account found associated with this user.');
                }
            }

            return result;
        } catch (error) {
            console.error('!!! 流程异常 !!!', error);
            throw error;
        }
    };

    // Connect to BigCommerce using Nango
    const connectBigCommerce = async (connectionConfig?: any) => {
        try {
            const token = (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) {
                throw new Error('No auth token found');
            }

            const requestBody = { 
                integration_id: 'bigcommerce',
                ...connectionConfig 
            };

            // Unified Request: Whether Stage 1 (Redirect) or Stage 3 (Token), call the endpoint once.
            const tokenResponse = await fetch(`${import.meta.env.VITE_OAUTH_HOST}/api/nango/connect-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            const tokenText = await tokenResponse.text();

            if (!tokenResponse.ok) throw new Error(`Token请求失败: ${tokenText}`);

            const tokenBody = JSON.parse(tokenText);

            // Case A: Handle REDIRECT action (Stage 1 - App Install)
            if (tokenBody.action === 'REDIRECT' && tokenBody.url) {
                window.location.href = tokenBody.url;
                return { action: 'REDIRECT' };
            }

            // Case B: Handle Token response (Stage 3 - Connect)
            const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

            if (!nangoToken) throw new Error('未获取到 Token');

            const nango = new Nango({ connectSessionToken: nangoToken });
            
            let result;
            try {
                // Pass connectionConfig (containing store_hash) as params
                const authParams = { 
                    params: {
                        storeHash: connectionConfig?.store_hash || 'zooye8wllz',
                        accountUuid: connectionConfig?.accountUuid || '7a74c338-861e-4087-9284-f79a8832440a'
                    } 
                };

                const connectionId = connectionConfig?.store_hash;
                result = await nango.auth('bigcommerce', connectionId, authParams as any);
            } catch (err: any) {
                if (err.message && err.message.includes('Modal blocked')) {
                    await fetchUserIntegrations();
                    return { connectionId: 'check_status_via_api' };
                }
                throw err;
            }

            if (result?.connectionId) {
                // Polling for connection status (max 3 attempts)
                // Note: Do not setBigCommerceConnected(true) here. Let fetchUserIntegrations update the state.
                // This keeps the UI in "loading" state (via the async await) until the backend confirms connection, preventing flickering.
                for (let i = 0; i < 3; i++) {
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s
                    const status = await fetchUserIntegrations();
                    if (status?.bigCommerce) {
                        break; // Connected
                    }
                }
            }

            return result;
        } catch (error) {
            console.error('[connectBigCommerce] BigCommerce 连接异常:', error);
            throw error;
        }
    };

    // Disconnect BigCommerce
    const disconnectBigCommerce = async () => {
        try {
            const token = (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) return;

            const response = await fetch(`${import.meta.env.VITE_OAUTH_HOST}/api/nango/unbind`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({integration_id: 'bigcommerce'})
            });

            if (response.ok) {
                setBigCommerceConnected(false);
                fetchUserIntegrations();
            } else {
                console.error('BigCommerce 解绑失败');
            }
        } catch (error) {
            console.error('BigCommerce 解绑异常:', error);
        }
    };


    // Disconnect Google Ads
    const disconnectGoogleAds = async () => {
        try {
            const token = (await supabase.auth.getSession()).data.session?.access_token;
            if (!token) return;

            const response = await fetch(`${import.meta.env.VITE_OAUTH_HOST}/api/nango/unbind`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({integration_id: 'google-ads'})
            });

            if (response.ok) {
                setGoogleAdsConnected(false);
                setIntegrationData(null);
                setGoogleAdsAvailableCustomers([]);
                setGoogleAdsHasFetchedAccounts(false);
            } else {
                console.error('解绑失败');
            }
        } catch (error) {
            console.error('解绑异常:', error);
        }
    };

    return {
        googleAds: {
            connected: googleAdsConnected,
            availableCustomers: googleAdsAvailableCustomers,
            hasFetchedAccounts: googleAdsHasFetchedAccounts,
            accountsLoading: googleAdsAccountsLoading,
        },
        bigCommerce: {
            connected: bigCommerceConnected,
            data: bigCommerceData,
        },
        connectGoogleAds,
        disconnectGoogleAds,
        connectBigCommerce,
        disconnectBigCommerce,
        integrationData
    };
};